import { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuthFetch } from '../lib/useAuthFetch';
import { Card, Pill } from './ui';

const API = import.meta.env.VITE_API_URL ?? '';

// ============================================================
// Alert rules — per-rule change notifications
// ============================================================
// The management surface for saved alert rules. Lives inside Notification
// settings (a dedicated "Alert rules" panel), visually separate from the global
// notification preferences. Three closed MVP rule types only — the form adapts
// its condition fields to the chosen type. Backed by /api/v1/alert-rules.

type RuleType = 'price_undercut' | 'shared_brand_added' | 'markdown_activity';
type Channel = 'email' | 'sms' | 'both';
type Frequency = 'instant' | 'daily';

interface RuleConfig {
  threshold_type?: 'any' | 'amount';
  threshold_amount?: number | null;
  carry_filter?: 'we_carry' | 'any';
}

interface AlertRule {
  id: string;
  name: string;
  rule_type: RuleType;
  location_id: string | null;
  location_name: string | null;
  competitor_id: string | null;
  competitor_name: string | null;
  brand_filter: string | null;
  config: RuleConfig;
  channel: Channel;
  frequency: Frequency;
  enabled: boolean;
  recipients: string[];
}

interface Recipient {
  user_id: string;
  name: string;
  email: string | null;
  unresolved?: boolean;
}
interface ScopeOpt {
  id: string;
  name: string;
}

const RULE_META: Record<RuleType, { label: string; desc: string; icon: string; tone: 'accent' | 'shared' | 'blocking' }> = {
  price_undercut: {
    label: 'Price undercut',
    desc: 'Notify when a tracked rival prices below your price.',
    icon: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    tone: 'accent',
  },
  shared_brand_added: {
    label: 'Shared brand added',
    desc: 'Notify when a tracked rival starts carrying a brand you also carry.',
    icon: 'M20 7h-3.5l-1-2h-7l-1 2H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM12 17a4 4 0 1 1 0-8 4 4 0 0 1 0 8z',
    tone: 'shared',
  },
  markdown_activity: {
    label: 'Markdown activity',
    desc: 'Notify when a tracked rival starts discounting items you also carry.',
    icon: 'M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4M4 6v12a2 2 0 0 0 2 2h14v-4M18 12a2 2 0 0 0 0 4h4v-4z',
    tone: 'blocking',
  },
};

// SMS is deferred until launch — legacy sms/both rules render honestly (they do
// not deliver on SMS today; 'both' still delivers Email, 'sms' delivers nothing).
const CHANNEL_LABEL: Record<Channel, string> = {
  email: 'Email',
  sms: 'SMS deferred until launch',
  both: 'Email now · SMS at launch',
};
const FREQ_LABEL: Record<Frequency, string> = { instant: 'Instant', daily: 'Daily digest' };

// ── local controls ─────────────────────────────────────────────────────────
function Seg<T extends string>({ options, value, onChange, testid }: { options: { value: T; label: string; disabled?: boolean }[]; value: T; onChange: (v: T) => void; testid?: string }) {
  return (
    <div data-testid={testid} style={{ display: 'inline-flex', background: 'var(--surface-3)', border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: 2, gap: 2 }}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={o.disabled}
          onClick={() => !o.disabled && onChange(o.value)}
          aria-pressed={value === o.value}
          title={o.disabled ? 'Available at launch' : undefined}
          style={{ padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: o.disabled ? 'not-allowed' : 'pointer', border: 'none', color: o.disabled ? 'var(--text-3)' : value === o.value ? '#fff' : 'var(--text-2)', background: value === o.value && !o.disabled ? 'var(--accent)' : 'transparent', opacity: o.disabled ? 0.55 : 1, fontFamily: 'var(--sans)', whiteSpace: 'nowrap' }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const selStyle: React.CSSProperties = {
  padding: '7px 26px 7px 10px', borderRadius: 'var(--r-sm)', border: '1.5px solid var(--border-2)',
  background: 'var(--surface)', color: 'var(--text-1)', fontFamily: 'var(--sans)', fontSize: 12, cursor: 'pointer',
  appearance: 'none', WebkitAppearance: 'none', outline: 'none', width: '100%',
  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' fill='none' stroke='%238aafc8' stroke-width='2.5' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '12px',
};
const inputStyle: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 'var(--r-sm)', border: '1.5px solid var(--border-2)',
  background: 'var(--surface)', color: 'var(--text-1)', fontFamily: 'var(--sans)', fontSize: 12, outline: 'none', width: '100%',
};

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 700, marginBottom: 7 }}>{children}</div>;
}

function Toggle({ on, onChange, testid }: { on: boolean; onChange: (v: boolean) => void; testid?: string }) {
  return (
    <button
      type="button"
      data-testid={testid}
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      style={{ width: 38, height: 21, background: on ? 'var(--accent)' : 'var(--border-2)', borderRadius: 11, position: 'relative', cursor: 'pointer', border: 'none', flexShrink: 0, padding: 0, transition: 'background 0.2s' }}
    >
      <div style={{ position: 'absolute', top: 3, left: on ? 20 : 3, width: 15, height: 15, background: '#fff', borderRadius: '50%', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
    </button>
  );
}

// ── blank draft ─────────────────────────────────────────────────────────────
function blankDraft(): AlertRule {
  return {
    id: '', name: '', rule_type: 'price_undercut',
    location_id: null, location_name: null, competitor_id: null, competitor_name: null,
    brand_filter: '', config: { threshold_type: 'any', threshold_amount: null },
    channel: 'email', frequency: 'instant', enabled: true, recipients: [],
  };
}

export default function AlertRules() {
  const authFetch = useAuthFetch();
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [roster, setRoster] = useState<Recipient[]>([]);
  const [locations, setLocations] = useState<ScopeOpt[]>([]);
  const [rivals, setRivals] = useState<ScopeOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  const [editing, setEditing] = useState<AlertRule | null>(null); // modal open when non-null
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AlertRule | null>(null);

  const showToast = (m: string) => { setToast(m); setTimeout(() => setToast(null), 2600); };

  const rosterById = useMemo(() => {
    const m = new Map<string, Recipient>();
    roster.forEach((r) => m.set(r.user_id, r));
    return m;
  }, [roster]);

  const loadRules = useCallback(async () => {
    const r = await authFetch(`${API}/api/v1/alert-rules`).then((x) => x.json());
    if (r.success) setRules(r.data.rules);
  }, [authFetch]);

  useEffect(() => {
    (async () => {
      try {
        const [rl, rc, sc] = await Promise.all([
          authFetch(`${API}/api/v1/alert-rules`).then((x) => x.json()),
          authFetch(`${API}/api/v1/alert-rules/recipients`).then((x) => x.json()),
          authFetch(`${API}/api/v1/alert-rules/scope`).then((x) => x.json()),
        ]);
        if (rl.success) setRules(rl.data.rules);
        if (rc.success) setRoster(rc.data.recipients);
        if (sc.success) { setLocations(sc.data.locations); setRivals(sc.data.rivals); }
      } catch {
        /* surfaced via empty UI + toast on actions */
      } finally {
        setLoading(false);
      }
    })();
  }, [authFetch]);

  // ── save (create or update) ────────────────────────────────────────────────
  const save = async () => {
    if (!editing) return;
    const d = editing;
    if (!d.name.trim()) { setFormErr('Give this rule a name.'); return; }
    if (d.recipients.length === 0) { setFormErr('Select at least one recipient.'); return; }

    // Only send condition fields relevant to the chosen type.
    const config: RuleConfig = {};
    let brand: string | null = null;
    if (d.rule_type === 'price_undercut') {
      config.threshold_type = d.config.threshold_type ?? 'any';
      config.threshold_amount = config.threshold_type === 'amount' ? Number(d.config.threshold_amount) || 0 : null;
      brand = d.brand_filter?.trim() || null;
    } else if (d.rule_type === 'shared_brand_added') {
      brand = d.brand_filter?.trim() || null;
    } else if (d.rule_type === 'markdown_activity') {
      config.carry_filter = d.config.carry_filter ?? 'we_carry';
    }

    const body = {
      name: d.name.trim(), rule_type: d.rule_type,
      location_id: d.location_id, competitor_id: d.competitor_id,
      // SMS is deferred until launch — every saved rule delivers by Email until
      // then, so legacy 'sms'/'both' drafts persist honestly as 'email'.
      brand_filter: brand, config, channel: 'email', frequency: d.frequency,
      enabled: d.enabled, recipients: d.recipients,
    };

    setSaving(true); setFormErr(null);
    try {
      const isEdit = Boolean(d.id);
      const res = await authFetch(`${API}/api/v1/alert-rules${isEdit ? `/${d.id}` : ''}`, {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (!res.ok || !j.success) {
        setFormErr(j.error || 'Could not save this rule. Check the fields and try again.');
        return;
      }
      await loadRules();
      setEditing(null);
      showToast(isEdit ? 'Alert rule updated' : 'Alert rule created');
    } catch {
      setFormErr('Could not reach the server. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (rule: AlertRule) => {
    const next = !rule.enabled;
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: next } : r))); // optimistic
    try {
      const res = await authFetch(`${API}/api/v1/alert-rules/${rule.id}/enabled`, {
        method: 'PATCH', body: JSON.stringify({ enabled: next }),
      });
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error();
      showToast(next ? 'Rule enabled' : 'Rule paused');
    } catch {
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: rule.enabled } : r))); // revert
      showToast('Could not update the rule');
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      const res = await authFetch(`${API}/api/v1/alert-rules/${confirmDelete.id}`, { method: 'DELETE' });
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error();
      setRules((prev) => prev.filter((r) => r.id !== confirmDelete.id));
      showToast('Alert rule deleted');
    } catch {
      showToast('Could not delete the rule');
    } finally {
      setConfirmDelete(null);
    }
  };

  const recipientNames = (ids: string[]) =>
    ids.map((id) => rosterById.get(id)?.name ?? `User ${id.slice(-6)}`);

  const scopeSummary = (r: AlertRule) => {
    const loc = r.location_id ? r.location_name ?? 'One location' : 'All locations';
    const riv = r.competitor_id ? r.competitor_name ?? 'One rival' : 'All tracked rivals';
    return `${loc} · ${riv}`;
  };

  return (
    <div data-testid="alert-rules">
      {/* Panel header — title, one-line explainer, primary CTA */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, background: 'var(--surface-2)', borderBottom: rules.length ? '1px solid var(--border)' : 'none' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 3 }}>Alert rules</div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, maxWidth: 560 }}>
              Get notified about specific competitive changes — a rival undercutting your price, adding a brand you carry, or discounting a shared product — and choose exactly who on your team hears about it.
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5, marginTop: 7, maxWidth: 560 }}>
              Live delivery is active for <strong style={{ color: 'var(--text-2)' }}>Price undercut</strong>, <strong style={{ color: 'var(--text-2)' }}>Shared brand added</strong>, and <strong style={{ color: 'var(--text-2)' }}>Markdown activity</strong> on <strong style={{ color: 'var(--text-2)' }}>Email</strong>, at both <strong style={{ color: 'var(--text-2)' }}>Instant</strong> and <strong style={{ color: 'var(--text-2)' }}>Daily digest</strong> (one grouped update per day). <strong style={{ color: 'var(--text-2)' }}>SMS will be enabled at launch.</strong>
            </div>
          </div>
          <button
            className="btn btn-primary"
            data-testid="ar-create"
            style={{ fontSize: 12, padding: '8px 14px', flexShrink: 0 }}
            onClick={() => { setFormErr(null); setEditing(blankDraft()); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 13, height: 13 }}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Create alert rule
          </button>
        </div>

        {/* Saved rules list — the dominant surface once rules exist */}
        {loading ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-3)' }}>Loading your alert rules…</div>
        ) : rules.length === 0 ? (
          <div data-testid="ar-empty" style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--accent-soft)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" style={{ width: 22, height: 22 }}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No alert rules yet</div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, maxWidth: 420, margin: '0 auto 18px' }}>
              Alert rules watch for specific competitive moves and notify the teammates you pick — the moment a rival undercuts you, stocks a brand you carry, or starts a markdown on a shared product.
            </div>
            <button className="btn btn-primary" data-testid="ar-empty-create" style={{ fontSize: 12, padding: '8px 16px' }} onClick={() => { setFormErr(null); setEditing(blankDraft()); }}>
              Create your first alert rule
            </button>
          </div>
        ) : (
          <div>
            {rules.map((r) => {
              const meta = RULE_META[r.rule_type];
              const names = recipientNames(r.recipients);
              const recipLabel = names.length === 0 ? 'No recipients'
                : names.length <= 2 ? names.join(', ')
                : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
              return (
                <div key={r.id} data-testid="ar-card" data-rule-id={r.id}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 20px', borderBottom: '1px solid var(--border)', opacity: r.enabled ? 1 : 0.62 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" style={{ width: 15, height: 15 }}><path d={meta.icon} /></svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-1)' }}>{r.name}</span>
                      <Pill tone={meta.tone} variant="chip" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>{meta.label}</Pill>
                      {!r.enabled && <Pill tone="neutral" variant="chip" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>Paused</Pill>}
                      {r.channel === 'sms' && <Pill tone="neutral" variant="chip" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>Unavailable until launch</Pill>}
                      {r.channel === 'both' && <Pill tone="neutral" variant="chip" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>SMS at launch</Pill>}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginBottom: 6 }}>{scopeSummary(r)}{r.brand_filter ? ` · brand: ${r.brand_filter}` : ''}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--text-3)' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 11, height: 11 }}><path d="M4 4h16v16H4z" /><path d="m22 6-10 7L2 6" /></svg>
                        {CHANNEL_LABEL[r.channel]}
                      </span>
                      <span>{FREQ_LABEL[r.frequency]}</span>
                      <span data-testid="ar-card-recipients" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 11, height: 11 }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></svg>
                        {r.recipients.length} {r.recipients.length === 1 ? 'recipient' : 'recipients'} · {recipLabel}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <Toggle on={r.enabled} onChange={() => toggleEnabled(r)} testid="ar-toggle" />
                    <button data-testid="ar-edit" title="Edit rule" onClick={() => { setFormErr(null); setEditing({ ...r, brand_filter: r.brand_filter ?? '', config: r.config ?? {} }); }}
                      style={{ background: 'none', border: '1px solid var(--border-2)', borderRadius: 6, padding: 6, cursor: 'pointer', color: 'var(--text-2)', display: 'flex' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 13, height: 13 }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></svg>
                    </button>
                    <button data-testid="ar-delete" title="Delete rule" onClick={() => setConfirmDelete(r)}
                      style={{ background: 'none', border: '1px solid var(--border-2)', borderRadius: 6, padding: 6, cursor: 'pointer', color: 'var(--rose)', display: 'flex' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 13, height: 13 }}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {editing && (
        <RuleModal
          draft={editing}
          setDraft={setEditing}
          roster={roster}
          locations={locations}
          rivals={rivals}
          saving={saving}
          formErr={formErr}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}

      {confirmDelete && (
        <div style={overlay} onClick={() => setConfirmDelete(null)}>
          <Card style={{ width: 'min(420px, 92vw)', padding: 22 }} onClick={() => { /* stop */ }}>
            <div onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 8 }}>Delete this alert rule?</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 20 }}>
                “{confirmDelete.name}” will stop watching for changes and its recipients will no longer be notified. This can't be undone.
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn" style={{ fontSize: 12 }} onClick={() => setConfirmDelete(null)}>Keep rule</button>
                <button data-testid="ar-delete-confirm" className="btn" style={{ fontSize: 12, background: 'var(--rose)', color: '#fff', borderColor: 'var(--rose)' }} onClick={doDelete}>Delete rule</button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--text-1)', color: 'var(--surface)', padding: '9px 18px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: 'var(--mono)', zIndex: 9000, letterSpacing: '0.04em' }}>
          {toast}
        </div>
      )}
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(4,10,14,0.55)', backdropFilter: 'blur(2px)',
  zIndex: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};

// ── Create / edit modal ─────────────────────────────────────────────────────
function RuleModal({
  draft, setDraft, roster, locations, rivals, saving, formErr, onSave, onCancel,
}: {
  draft: AlertRule;
  setDraft: (r: AlertRule) => void;
  roster: Recipient[];
  locations: ScopeOpt[];
  rivals: ScopeOpt[];
  saving: boolean;
  formErr: string | null;
  onSave: () => void;
  onCancel: () => void;
}) {
  const meta = RULE_META[draft.rule_type];
  const patch = (p: Partial<AlertRule>) => setDraft({ ...draft, ...p });
  const patchConfig = (p: Partial<RuleConfig>) => setDraft({ ...draft, config: { ...draft.config, ...p } });
  const toggleRecipient = (id: string) => {
    const has = draft.recipients.includes(id);
    patch({ recipients: has ? draft.recipients.filter((x) => x !== id) : [...draft.recipients, id] });
  };

  const showBrand = draft.rule_type === 'price_undercut' || draft.rule_type === 'shared_brand_added';
  const showThreshold = draft.rule_type === 'price_undercut';
  const showCarry = draft.rule_type === 'markdown_activity';

  return (
    <div style={overlay} onClick={onCancel}>
      <Card data-testid="ar-modal" style={{ width: 'min(600px, 94vw)', maxHeight: '90vh', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
          {/* Modal header */}
          <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', flexShrink: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>{draft.id ? 'Edit alert rule' : 'New alert rule'}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginTop: 2 }}>Notify the right people the moment a rival makes a move you care about.</div>
          </div>

          {/* Modal body — fields in required order */}
          <div style={{ padding: 22, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Name */}
            <div>
              <FieldLabel>Rule name</FieldLabel>
              <input data-testid="ar-name" style={inputStyle} placeholder="e.g. STIIIZY undercut at Corona" value={draft.name} onChange={(e) => patch({ name: e.target.value })} />
            </div>

            {/* 1 — Rule type */}
            <div>
              <FieldLabel>1 · Rule type</FieldLabel>
              <div style={{ display: 'grid', gap: 8 }}>
                {(Object.keys(RULE_META) as RuleType[]).map((t) => {
                  const m = RULE_META[t];
                  const on = draft.rule_type === t;
                  return (
                    <button key={t} type="button" data-testid={`ar-type-${t}`} onClick={() => patch({ rule_type: t })}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 11, textAlign: 'left', padding: '11px 13px', borderRadius: 'var(--r-sm)', cursor: 'pointer', background: on ? 'var(--accent-soft)' : 'var(--surface)', border: on ? '1.5px solid var(--accent)' : '1.5px solid var(--border-2)' }}>
                      <div style={{ width: 26, height: 26, borderRadius: 7, background: on ? 'var(--accent)' : 'var(--surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke={on ? '#fff' : 'var(--text-2)'} strokeWidth="2" style={{ width: 14, height: 14 }}><path d={m.icon} /></svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-1)' }}>{m.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 1 }}>{m.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 2 — Scope */}
            <div>
              <FieldLabel>2 · Scope</FieldLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 5 }}>Location</div>
                  <select data-testid="ar-scope-location" style={selStyle} value={draft.location_id ?? ''} onChange={(e) => patch({ location_id: e.target.value || null })}>
                    <option value="">All locations</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 5 }}>Rival</div>
                  <select data-testid="ar-scope-rival" style={selStyle} value={draft.competitor_id ?? ''} onChange={(e) => patch({ competitor_id: e.target.value || null })}>
                    <option value="">All tracked rivals</option>
                    {rivals.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* 3 — Condition (adapts to rule type) */}
            <div>
              <FieldLabel>3 · Condition</FieldLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {showBrand && (
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 5 }}>Brand {draft.rule_type === 'price_undercut' ? '(optional — leave blank for any brand)' : '(optional — leave blank for any shared brand)'}</div>
                    <input data-testid="ar-brand" style={inputStyle} placeholder="e.g. STIIIZY" value={draft.brand_filter ?? ''} onChange={(e) => patch({ brand_filter: e.target.value })} />
                  </div>
                )}
                {showThreshold && (
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 5 }}>Undercut amount</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <Seg testid="ar-threshold-type" value={draft.config.threshold_type ?? 'any'} onChange={(v) => patchConfig({ threshold_type: v })}
                        options={[{ value: 'any', label: 'Any undercut' }, { value: 'amount', label: 'By at least $X' }]} />
                      {draft.config.threshold_type === 'amount' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-3)' }}>$</span>
                          <input data-testid="ar-threshold-amount" type="number" min={0} step="0.5" style={{ ...inputStyle, width: 90 }} value={draft.config.threshold_amount ?? ''} onChange={(e) => patchConfig({ threshold_amount: e.target.value === '' ? null : Number(e.target.value) })} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {showCarry && (
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 5 }}>Which markdowns</div>
                    <Seg testid="ar-carry" value={draft.config.carry_filter ?? 'we_carry'} onChange={(v) => patchConfig({ carry_filter: v })}
                      options={[{ value: 'we_carry', label: 'Only products we carry' }, { value: 'any', label: 'Any markdown' }]} />
                  </div>
                )}
                {draft.rule_type === 'shared_brand_added' && (
                  <div style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>No extra thresholds — this rule fires whenever the rival adds a matching brand.</div>
                )}
              </div>
            </div>

            {/* 4 — Delivery */}
            <div>
              <FieldLabel>4 · Delivery</FieldLabel>
              <Seg testid="ar-channel" value={draft.channel === 'email' ? 'email' : 'email'} onChange={(v) => patch({ channel: v })}
                options={[{ value: 'email', label: 'Email' }, { value: 'sms', label: 'SMS · at launch', disabled: true }]} />
              <div style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 7, fontFamily: 'var(--mono)' }}>
                Email alerts are live now. SMS will be enabled at launch.
              </div>
              {(draft.channel === 'sms' || draft.channel === 'both') && (
                <div style={{ fontSize: 10.5, color: 'var(--warm)', marginTop: 5, fontFamily: 'var(--mono)' }}>
                  This rule was set to {draft.channel === 'both' ? '“Both”' : '“SMS”'} before SMS launch — saving will deliver it by <strong>Email</strong> until SMS goes live.
                </div>
              )}
            </div>

            {/* 5 — Recipients */}
            <div>
              <FieldLabel>5 · Recipients</FieldLabel>
              {roster.length === 0 ? (
                <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>No selectable users found for your organization.</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {roster.map((u) => {
                    const on = draft.recipients.includes(u.user_id);
                    return (
                      <button key={u.user_id} type="button" data-testid={`ar-recipient-${u.user_id}`} onClick={() => toggleRecipient(u.user_id)}
                        aria-pressed={on}
                        style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 11px', borderRadius: 20, cursor: 'pointer', fontSize: 11.5, fontWeight: 600, background: on ? 'var(--accent)' : 'var(--surface-3)', color: on ? '#fff' : 'var(--text-2)', border: on ? '1.5px solid var(--accent)' : '1.5px solid var(--border-2)' }}>
                        {on && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ width: 11, height: 11 }}><polyline points="20 6 9 17 4 12" /></svg>}
                        <span>{u.name}</span>
                        {u.email && <span style={{ opacity: 0.7, fontWeight: 400, fontFamily: 'var(--mono)', fontSize: 10 }}>{u.email}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 6 — Frequency */}
            <div>
              <FieldLabel>6 · Frequency</FieldLabel>
              <Seg testid="ar-frequency" value={draft.frequency} onChange={(v) => patch({ frequency: v })}
                options={[{ value: 'instant', label: 'Instant' }, { value: 'daily', label: 'Daily digest' }]} />
            </div>

            {formErr && (
              <div data-testid="ar-form-error" style={{ fontSize: 11.5, color: 'var(--rose)', background: 'var(--rose-soft)', padding: '8px 12px', borderRadius: 'var(--r-sm)' }}>{formErr}</div>
            )}
          </div>

          {/* Modal footer — Save action */}
          <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0, background: 'var(--surface-2)' }}>
            <button className="btn" data-testid="ar-cancel" style={{ fontSize: 12 }} onClick={onCancel} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" data-testid="ar-save" style={{ fontSize: 12 }} onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : draft.id ? 'Save changes' : 'Create alert rule'}
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
